import React, { useState } from 'react';
import { Box, Typography, LinearProgress, Chip, Button, Collapse, Alert } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';

const STATUS_META = {
  not_started: { label: 'Not split yet', color: 'default' },
  starting: { label: 'Starting…', color: 'primary' },
  pending: { label: 'Queued…', color: 'default' },
  processing: { label: 'Splitting…', color: 'primary' },
  success: { label: 'Split complete', color: 'success' },
  failed: { label: 'Ready to re-run', color: 'warning' },
  error: { label: 'Failed', color: 'error' },
};

// the reason slugs the API returns for rows it could not split
const SKIP_REASON_LABEL = {
  no_contract: 'no contract ratio',
  no_song: 'ISRC not in the catalogue',
};

const formatCount = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0');
// earnings arrive as fixed-2dp strings because they are payouts, never as floats
const formatMoney = (value) => `₹${value === 0 || value ? value : '0.00'}`;

export const downloadSplitErrorLog = (errorLog) => {
  const blob = new Blob([errorLog || ''], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'revenue-split-errors.txt';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function SplitProgress({ status, splitStatus, message, error, starting }) {
  const [showTracks, setShowTracks] = useState(false);

  // the request is still in flight: no status exists yet, but the user pressed the
  // button and must see something happening straight away
  const state = starting ? 'starting' : (splitStatus || (status && status.split_status) || 'pending');
  const errorLog = (status && status.error_log) || '';
  // a failed status with an empty error_log is an operator resetting it to allow a
  // re-run, not a crash, so it is only a hard failure when there is something to read
  const isHardFailure = state === 'failed' && !!errorLog;
  const meta = error || isHardFailure ? STATUS_META.error : (STATUS_META[state] || STATUS_META.pending);

  const totalRows = Number(status && status.total_rows) || 0;
  const percent = state === 'success'
    ? 100
    : Math.min(100, Math.max(0, Number(status && status.progress_percent) || 0));
  // no row count yet (still starting or queued) -> no meaningful percentage to show
  const indeterminate = state === 'starting' || (state === 'pending' && !totalRows);

  const skipped = (status && status.skipped) || null;
  const skippedRows = Number(skipped && skipped.rows) || 0;
  const tracks = (skipped && skipped.tracks) || [];
  const awaiting = Number(status && status.rows_awaiting_split) || 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CallSplitIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Revenue split</Typography>
        <Chip size="small" label={meta.label} color={meta.color} />
      </Box>

      {error ? (
        <Typography variant="body2" color="error.main">{error}</Typography>
      ) : (
        <>
          {state !== 'success' && state !== 'failed' && (
            <>
              <LinearProgress
                variant={indeterminate ? 'indeterminate' : 'determinate'}
                value={percent}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {state === 'starting' && 'Sending the split request…'}
                  {state !== 'starting' && indeterminate && 'Waiting for a worker to pick it up…'}
                  {!indeterminate && `${formatCount(status && status.rows_processed)} / ${formatCount(totalRows)} rows`}
                </Typography>
                {!indeterminate && (
                  <Typography variant="caption" color="text.secondary">{percent.toFixed(1)}%</Typography>
                )}
              </Box>
            </>
          )}

          {state === 'success' && (
            <Typography variant="body2" color="success.main">
              {totalRows
                ? `Split ${formatCount(status && status.rows_shared)} of ${formatCount(totalRows)} rows into revenue shares.`
                : 'This month has already been split.'}
            </Typography>
          )}

          {state === 'failed' && !errorLog && (
            <Typography variant="body2" color="warning.main">
              This split was reset so it can be run again. Re-running only credits the
              difference, so nobody is paid twice.
            </Typography>
          )}

          {state === 'failed' && errorLog && (
            <Box>
              <Typography variant="body2" color="error.main">The split did not finish.</Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  maxHeight: 150,
                  overflow: 'auto',
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                }}
              >
                {errorLog}
              </Box>
              <Button size="small" startIcon={<DownloadIcon />} sx={{ mt: 1 }}
                onClick={() => downloadSplitErrorLog(errorLog)}>
                Download error log
              </Button>
            </Box>
          )}
        </>
      )}

      {/* most skips are worth nothing, so the money is what decides whether to alarm */}
      {!error && skippedRows > 0 && (
        <Alert
          severity={Number(skipped.earnings_not_split) > 0 ? 'warning' : 'info'}
          sx={{ mt: 1.5, py: 0.5 }}
        >
          <Typography variant="body2">
            {`${formatCount(skippedRows)} rows (${formatMoney(skipped.earnings_not_split)}) could not be split`}
            {tracks.length ? ` — ${tracks.length} track${tracks.length > 1 ? 's' : ''} affected` : ''}
          </Typography>
          {tracks.length > 0 && (
            <>
              <Button
                size="small"
                sx={{ mt: 0.5, pl: 0 }}
                endIcon={showTracks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowTracks(!showTracks)}
              >
                {showTracks ? 'Hide tracks' : 'Show tracks'}
              </Button>
              <Collapse in={showTracks}>
                <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
                  {tracks.map((track) => (
                    <Box component="li" key={`${track.isrc_code}-${track.reason}`} sx={{ mb: 0.5 }}>
                      <Typography variant="caption" component="div">
                        <strong>{track.track_title || 'Untitled'}</strong>
                        {track.isrc_code ? ` (${track.isrc_code})` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {`${formatCount(track.rows)} rows · ${formatMoney(track.earnings)} · `}
                        {SKIP_REASON_LABEL[track.reason] || track.reason}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </>
          )}
        </Alert>
      )}

      {/* a run was cut short or ran against stale worker code */}
      {!error && awaiting > 0 && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
          <Typography variant="body2">
            {`${formatCount(awaiting)} rows still need splitting — run the split again.`}
          </Typography>
        </Alert>
      )}

      {!error && message && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{message}</Typography>
      )}
    </Box>
  );
}
